import { MessageSquare } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent } from "../ui/Card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import { formatCodeOrDash } from "./patientVisitFields";

export function formatStarGlyphs(rating) {
  if (rating == null) return null;
  const filled = Math.max(0, Math.min(5, Math.round(Number(rating))));
  return `${"★".repeat(filled)}${"☆".repeat(5 - filled)}`;
}

export function CommentIconButton({ onOpen, label = "View comment" }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-6 w-6 text-ktx-navy"
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.();
      }}
    >
      <MessageSquare className="h-4 w-4" />
    </Button>
  );
}

export function RatingWithComment({
  rating,
  additionalFeedback,
  onOpenComment,
  stacked = false,
}) {
  const hasComment = Boolean(additionalFeedback);
  const stars = formatStarGlyphs(rating);

  if (rating == null && !hasComment) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <span className={`inline-flex ${stacked ? "flex-col items-start gap-0.5" : "items-center justify-center gap-1.5"}`}>
      {stars ? (
        <span className="whitespace-nowrap leading-5 text-ktx-purple">{stars}</span>
      ) : (
        <span className="leading-5 text-gray-400">—</span>
      )}
      {hasComment && (
        <CommentIconButton onOpen={onOpenComment} label="View rating comment" />
      )}
    </span>
  );
}

export function CodeWithRationale({
  code,
  rationale,
  onOpenRationale,
  stacked = false,
}) {
  const hasRationale = Boolean(rationale);

  return (
    <span className={`inline-flex ${stacked ? "flex-col items-start gap-0.5" : "items-center justify-center gap-1.5"}`}>
      <span>{formatCodeOrDash(code)}</span>
      {hasRationale && (
        <CommentIconButton onOpen={onOpenRationale} label="View E&M override rationale" />
      )}
    </span>
  );
}

function CommentDialog({
  open,
  onOpenChange,
  title,
  patientName,
  mrn,
  children,
}) {
  const subtitle = [patientName, mrn ? `MRN: ${mrn}` : null].filter(Boolean).join(" · ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {subtitle ? <DialogDescription>{subtitle}</DialogDescription> : null}
        </DialogHeader>
        <Card>
          <CardContent className="p-4">{children}</CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}

export function OverrideRationaleModal({
  open,
  onOpenChange,
  patientName,
  mrn,
  finalCode,
  knowtexCode,
  overrideRationale,
}) {
  return (
    <CommentDialog
      open={open}
      onOpenChange={onOpenChange}
      title="E&M Override Rationale"
      patientName={patientName}
      mrn={mrn}
    >
      <div className="space-y-3">
        <div className="flex gap-6 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Final E&M</div>
            <div className="font-semibold text-ktx-navy">{formatCodeOrDash(finalCode)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Knowtex E&M</div>
            <div className="font-semibold text-ktx-navy">{formatCodeOrDash(knowtexCode)}</div>
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Rationale</div>
          <div className="whitespace-pre-wrap text-sm text-ktx-navy">
            {overrideRationale || "No rationale left for this override."}
          </div>
        </div>
      </div>
    </CommentDialog>
  );
}

export default function VisitFeedbackModal({
  open,
  onOpenChange,
  patientName,
  mrn,
  rating,
  additionalFeedback,
}) {
  const stars = formatStarGlyphs(rating);

  return (
    <CommentDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Visit Feedback"
      patientName={patientName}
      mrn={mrn}
    >
      <div className="space-y-3">
        <div>
          <div className="text-xs text-muted-foreground">Rating</div>
          <div className="text-sm font-semibold text-ktx-purple">
            {stars || "—"}
            {rating != null ? (
              <span className="ml-2 text-sm font-normal text-ktx-navy">{rating} / 5</span>
            ) : null}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Comment</div>
          <div className="whitespace-pre-wrap text-sm text-ktx-navy">
            {additionalFeedback || "No comment left with this rating."}
          </div>
        </div>
      </div>
    </CommentDialog>
  );
}
